import { ReactNode } from 'react';
import { Alert, ButtonIcon } from '@backstage/ui';
import { Snackbar } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { createPortal } from 'react-dom';

// Guaranteed to render above any MUI Dialog/Modal, including nested ones, whose
// stacked z-indices can otherwise exceed a simple `theme.zIndex.modal + 1` offset.
const SNACKBAR_Z_INDEX = 9999;

type AlertSeverity = 'error' | 'info' | 'success' | 'warning';

const getAlertStatus = (severity: AlertSeverity): 'info' | 'success' | 'warning' | 'danger' => {
  if (severity === 'error') return 'danger';
  if (severity === 'warning') return 'warning';
  if (severity === 'success') return 'success';
  return 'info';
};

export interface TopSnackbarAlertProps {
  open: boolean;
  onClose: () => void;
  severity: AlertSeverity;
  ariaLabel: string;
  title?: string;
  autoHideDuration?: number | null;
  children: ReactNode;
}

/**
 * A Snackbar/Alert combination anchored to the top-center of the viewport with a
 * zIndex above Material-UI's default modal layer. Used to surface success, error,
 * and info messages so they remain visible above dialogs such as Manage Deployments.
 */
export const TopSnackbarAlert = ({
  open,
  onClose,
  severity,
  ariaLabel,
  title,
  autoHideDuration = 10000,
  children,
}: TopSnackbarAlertProps) => {
  return createPortal(
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      autoHideDuration={autoHideDuration}
      style={{
        zIndex: SNACKBAR_Z_INDEX,
        width: 'min(calc(100vw - 32px), 640px)',
      }}
      onClose={(_event, reason) => {
        if (reason === 'clickaway') return;
        onClose();
      }}
    >
      <div role="alert" aria-label={ariaLabel} style={{ width: '100%' }}>
        <Alert
          role="presentation"
          status={getAlertStatus(severity)}
          icon
          title={title ? <span style={{ color: '#fff', fontSize: '1rem', fontWeight: 700 }}>{title}</span> : undefined}
          description={
            <span style={{ color: '#fff', display: 'block', fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.4 }}>
              {children}
            </span>
          }
          style={{
            boxSizing: 'border-box',
            color: '#fff',
            width: '100%',
            boxShadow: '0 0 5px 1px #000',
            WebkitBoxShadow: '0 0 5px 1px #000',
          }}
          customActions={
            <ButtonIcon
              aria-label="Close"
              icon={<CloseIcon />}
              variant="tertiary"
              onClick={onClose}
              style={{ color: '#fff' }}
            />
          }
        />
      </div>
    </Snackbar>,
    document.body,
  );
};
